import { addAssetToContainer } from '../../libs/buildChildrenForScene';
import type { Interpolation } from '../../types';
import { Config } from '../../video-data/config';
import type { BaseAsset } from '../asset/BaseAsset';
import type { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import { BaseGroup } from '../BaseGroup';
import type { DynamicStylingMixinInterface } from '../dynamicStylingMixin';

export type MorphableProperty = 'position.x' | 'position.y' | 'scale.x' | 'scale.y' | 'alpha';

type MorphableAsset = BaseAsset & DynamicStylingMixinInterface;

type MorphSourceState = {
    asset: MorphableAsset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
};

export class MorphManager extends BaseGroup {
    override _classType = 'MorphManager';

    #sourceStates: Map<string, MorphSourceState> = new Map();

    override get propertiesToSerialize() {
        return [];
    }

    setupMorphAnimations() {
        const nextScene = this.slide.nextScene;
        if (!nextScene) {
            return;
        }

        const sourceAssets = this.slide
            .getAssets({ deep: true })
            .sort((a, b) => a.name.localeCompare(b.name));
        const targetAssets = (nextScene.getAssets({ deep: true }) as BaseAsset[]).sort((a, b) =>
            a.name.localeCompare(b.name),
        );

        const claimedTargetIds = new Set<string>();

        sourceAssets.forEach((sourceAsset) => {
            const matchingAsset = targetAssets.find(
                (targetAsset) =>
                    !claimedTargetIds.has(targetAsset.id) && sourceAsset.isMorphMatch(targetAsset),
            );
            if (!matchingAsset) {
                return;
            }

            claimedTargetIds.add(matchingAsset.id);
            this.#setupMorphForAssetPair(sourceAsset as MorphableAsset, matchingAsset);
        });

        for (const { asset } of this.#sourceStates.values()) {
            if (!asset.hasMorphAnimation) {
                continue;
            }
            MorphManager.#forceBuildAndDraw(asset);
        }

        this._childAssets.forEach((clone) => {
            const morphableClone = clone as MorphableAsset;
            if (!morphableClone.hasMorphAnimation) {
                return;
            }
            MorphManager.#forceBuildAndDraw(morphableClone);
        });
    }

    clearMorphAnimations() {
        for (const state of this.#sourceStates.values()) {
            const { asset, x, y, scaleX, scaleY, alpha } = state;

            if (!asset || asset.destroyed) {
                continue;
            }

            asset.clearMorphAnimations();

            asset.transformation.position.x = x;
            asset.transformation.position.y = y;
            asset.scale.x = scaleX;
            asset.scale.y = scaleY;
            asset.alpha = alpha;

            MorphManager.#forceBuildAndDraw(asset);
        }
        this.#sourceStates.clear();

        [...this._childAssets].forEach((clone) => {
            this.removeChild(clone);
            clone.destroy();
        });
    }

    #setupMorphForAssetPair(sourceAsset: MorphableAsset, targetAsset: BaseAsset) {
        const { startPoint, interpolation } = sourceAsset.morphParameters;
        const maxDuration = this.slide.duration / 2;
        const availableDuration = this.slide.duration * (1 - startPoint);
        const morphDuration = Math.min(availableDuration, maxDuration);

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
            });
        }

        const targetClone = this.#cloneAssetForMorph(targetAsset, sourceAsset);

        MorphManager.#setupAnimations(
            sourceAsset,
            targetAsset,
            targetClone,
            morphDuration,
            interpolation,
        );
    }

    #cloneAssetForMorph(targetAsset: BaseAsset, sourceAsset: BaseAsset): MorphableAsset {
        const savedData = targetAsset.save();

        const clone = addAssetToContainer(this, savedData.classType) as MorphableAsset;
        clone.load(savedData);

        if (
            Config.isMediaAsset(clone as TimeAwareMediaAsset) &&
            Config.isMediaAsset(sourceAsset as TimeAwareMediaAsset)
        ) {
            (clone as TimeAwareMediaAsset).linkSourceTo(sourceAsset as TimeAwareMediaAsset);
        }

        clone.transformation.position.x = sourceAsset.transformation.position.x;
        clone.transformation.position.y = sourceAsset.transformation.position.y;
        clone.transformation.anchor.x = sourceAsset.transformation.anchor.x;
        clone.transformation.anchor.y = sourceAsset.transformation.anchor.y;

        clone.alpha = 0;
        clone.autoDraw = sourceAsset.autoDraw;
        clone.morphParameters = { ...sourceAsset.morphParameters };

        return clone;
    }

    static #setupAnimations(
        sourceAsset: MorphableAsset,
        targetAsset: BaseAsset,
        targetClone: MorphableAsset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        // TODO: LU-3903 -- figure out if we just add #morphManager._update() from Slide childUpdated
        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const excludedProperties = sourceAsset.morphExcludedProperties;
        const animationConfigs = MorphManager.#buildAnimationConfigs(sourceAsset, targetAsset);

        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.source.startValue;
            sourceAnim.endValue = config.source.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation = interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;

            const cloneAnim = targetClone.addMorphAnimation();
            cloneAnim.property = config.property;
            cloneAnim.startValue = config.clone.startValue;
            cloneAnim.endValue = config.clone.endValue;
            cloneAnim.duration = morphDuration;
            cloneAnim.interpolation = interpolation;
            cloneAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #buildAnimationConfigs(sourceAsset: MorphableAsset, targetAsset: BaseAsset) {
        const sourcePos = sourceAsset.transformation.position;
        const targetPos = targetAsset.transformation.position;
        const deltaX = targetPos.x - sourcePos.x;
        const deltaY = targetPos.y - sourcePos.y;

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;
        const inverseScaleRatioX = scaleRatioX > 0 ? 1 / scaleRatioX : 1;
        const inverseScaleRatioY = scaleRatioY > 0 ? 1 / scaleRatioY : 1;

        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    source: { startValue: number; endValue: number };
                    clone: { startValue: number; endValue: number };
                }
            >
        > = {};

        if (deltaX !== 0) {
            configs['position.x'] = {
                property: 'transformation.position.x',
                shouldOffsetPropertyValue: true,
                source: { startValue: 0, endValue: deltaX },
                clone: { startValue: 0, endValue: deltaX },
            };
        }

        if (deltaY !== 0) {
            configs['position.y'] = {
                property: 'transformation.position.y',
                shouldOffsetPropertyValue: true,
                source: { startValue: 0, endValue: deltaY },
                clone: { startValue: 0, endValue: deltaY },
            };
        }

        if (scaleRatioX !== 1) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                source: { startValue: 1, endValue: scaleRatioX },
                clone: { startValue: inverseScaleRatioX, endValue: 1 },
            };
        }

        if (scaleRatioY !== 1) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                source: { startValue: 1, endValue: scaleRatioY },
                clone: { startValue: inverseScaleRatioY, endValue: 1 },
            };
        }

        configs['alpha'] = {
            property: 'alpha',
            shouldOffsetPropertyValue: false,
            source: { startValue: sourceAsset.alpha, endValue: 0 },
            clone: { startValue: 0, endValue: targetAsset.alpha },
        };

        return configs;
    }

    static #forceBuildAndDraw(asset: BaseAsset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }
}
